<template>
	<div>

		<h2>{{ txt.heading }}</h2>
		<v-expansion-panels>
			<v-expansion-panel>
				<v-expansion-panel-header>
					<v-row no-gutters>
						<v-col cols="4">
							<strong>{{ 'Existing rights (' + rights.length + ')' }}</strong>
						</v-col>
					</v-row>
				</v-expansion-panel-header>
				<v-expansion-panel-content>
					<v-data-table
						:headers="headers"
						:items="rights"
						class="elevation-1"
						:items-per-page="-1"
					>
						<template v-slot:item.selected_role_label="{ item }">
							{{ installationName(item.selected_role_value) }}
						</template>
						<template v-slot:item.actions="{ item }">
							<v-icon small @click="openDeleteDialog(item)">mdi-delete</v-icon>
						</template>
					</v-data-table>
				</v-expansion-panel-content>
			</v-expansion-panel>
		</v-expansion-panels>

		<v-divider class="dividerNewRights" />

		<v-row>
			<v-col>
				<v-autocomplete
					v-model="selectedReport"
					:items="availableReports"
					item-text="report_name"
					item-value="report_id"
					:label="txt.labels.report"
					placeholder="Select a Power BI report"
					prepend-icon="mdi-chart-box-plus-outline"
					clearable
					return-object
				/>
			</v-col>
			<v-col>
				<v-autocomplete
					v-model="selectedInstallation"
					:items="installations"
					item-text="text"
					item-value="value"
					:label="txt.labels.installation"
					placeholder="Select an installation (optional)"
					:disabled="!selectedReport || !selectedReport.rls_enabled"
					clearable
					return-object
				/>
			</v-col>
		</v-row>

		<v-btn class="mr-4 mb-4" color="primary" :disabled="!selectedReport" @click="addRight">
			{{ txt.btn.add }}
		</v-btn>

		<dialog-before-action
			:maxWidthOfPanel="400"
			:header="txt.dialog.deleteHeader"
			:model="show.deleteDialog"
			:key="show.deleteDialog"
			:payload="deletingRight"
			@primaryConfirmed="confirmDelete"
			@secondaryConfirmed="show.deleteDialog = false"
		/>
	</div>
</template>

<script>
/* global config */
import axios from 'axios'
import DialogBeforeAction from '../../arcCustom/DialogBeforeAction.vue'

export default {
	name: 'PowerBiSpecificRightsTable',
	components: {
		DialogBeforeAction,
	},
	props: {
		selectedUser: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			rights: [],
			reports: [],
			installations: [],
			selectedReport: null,
			selectedInstallation: null,
			show: { deleteDialog: false },
			deletingRight: null,
			txt: {
				heading: 'Power BI Rights',
				labels: {
					report: 'Report',
					installation: 'Installation',
				},
				btn: {
					add: 'Add Right',
				},
				dialog: {
					deleteHeader: 'Remove Right?',
				},
			},
		}
	},
	computed: {
		availableReports() {
			return this.reports.filter((r) => !this.rights.find((right) => right.report_id === r.report_id))
		},
		headers() {
			return [
				{ text: 'Report Name', value: 'report_name' },
				{ text: 'Installation', value: 'selected_role_label' },
				{ text: 'Actions', value: 'actions', sortable: false },
			]
		},
	},
	methods: {
		async fetchReports() {
			try {
				const res = await axios.get(`${config.baseAPIurl}/user-manager/power-bi-reports`)
				this.reports = res.data.filter((r) => r.report_enabled)
			} catch (error) {
				this.$emit('error', error)
			}
		},
		async fetchInstallations() {
			try {
				const res = await axios.get(`${config.baseAPIurl}/powerbi/installations`)
				this.installations = res.data
			} catch (error) {
				this.$emit('error', error)
			}
		},
		async fetchCurrentRights() {
			if (!this.selectedUser || !this.selectedUser.id) return
			try {
				const res = await axios.get(`${config.baseAPIurl}/user-manager/power-bi-rights?user_id=${this.selectedUser.id}`)
				this.rights = res.data.map((row) => ({
					report_id: row.report_id,
					report_name: row.report_name,
					selected_role_value: row.rls_column_value,
					selected_role_label: row.rls_column_label,
				}))
			} catch (error) {
				this.$emit('error', error)
			}
		},
		async addRight() {
			if (!this.selectedReport) return
			try {
				await axios.post(`${config.baseAPIurl}/user-manager/power-bi-rights`, {
					user_id: this.selectedUser.id,
					report_id: this.selectedReport.report_id,
					rls_column_value: this.selectedInstallation ? this.selectedInstallation.value : null,
				})
				this.rights.push({
					report_id: this.selectedReport.report_id,
					report_name: this.selectedReport.report_name,
					selected_role_value: this.selectedInstallation ? this.selectedInstallation.value : null,
					selected_role_label: this.selectedInstallation ? this.selectedInstallation.text : null,
				})
				this.selectedReport = null
				this.selectedInstallation = null
				this.$emit('rightAdded')
			} catch (error) {
				this.$emit('error', error)
			}
		},
		installationName(value) {
			if (value == null) return '-'
			const inst = this.installations.find((i) => String(i.value) === String(value))
			return inst ? inst.text : value
		},
		openDeleteDialog(item) {
			this.deletingRight = item
			this.show.deleteDialog = true
		},
		async confirmDelete(item) {
			try {
				await axios.delete(`${config.baseAPIurl}/user-manager/power-bi-rights`, {
					params: { user_id: this.selectedUser.id, report_id: item.report_id },
				})
				this.rights.splice(this.rights.indexOf(item), 1)
				this.show.deleteDialog = false
				this.$emit('rightDeleted')
			} catch (error) {
				this.$emit('error', error)
			}
		},
	},
	watch: {
		selectedReport(newVal) {
			if (!newVal || !newVal.rls_enabled) {
				this.selectedInstallation = null
			}
		},
		selectedUser: {
			handler() {
				this.rights = []
				this.fetchCurrentRights()
			},
		},
	},
	async created() {
		await Promise.all([this.fetchReports(), this.fetchInstallations(), this.fetchCurrentRights()])
	},
}
</script>

<style lang="scss" scoped>
.dividerNewRights {
	margin-top: 25px;
	margin-bottom: 10px;
}
</style>
